import React, { useMemo } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiTarget, FiTrendingUp, FiActivity, FiStar, FiShield } = FiIcons;

const MilestoneBox = ({ icon, title, value, sub, color }) => (
  <div className="border border-[#658d71] bg-[rgba(0,0,0,0.12)] p-3 flex flex-col items-center text-center relative overflow-hidden group hover:bg-[rgba(0,0,0,0.2)] transition-colors">
    <SafeIcon icon={icon} size={20} style={{ color }} className="mb-2" />
    <span className="text-[9px] font-black uppercase tracking-widest text-[#658d71] mb-1">{title}</span>
    <span className="text-2xl font-black tabular-nums text-white leading-none">{value}</span>
    {sub && <span className="text-[8px] text-[#a9c9b1] font-bold mt-1.5 uppercase tracking-widest leading-tight">{sub}</span>}
  </div>
);

const SeasonMilestones = ({ games }) => {
  const milestones = useMemo(() => {
    if (!games?.length) return null;
    const finalGames = games.filter(g => g.status?.abstractGameState === 'Final');
    if (!finalGames.length) return null;

    let maxWinStreak = 0, currentWinStreak = 0;
    let maxScoreStreak = 0, currentScoreStreak = 0;
    let mostRuns = 0;
    let largestMargin = 0;
    let shutouts = 0;

    finalGames.forEach(g => {
      const bosHome = g.teams?.home?.team?.id === 111;
      const bosScore = Number(bosHome ? g.teams.home.score : g.teams.away.score);
      const oppScore = Number(bosHome ? g.teams.away.score : g.teams.home.score);
      const won = bosScore > oppScore;

      // Win Streak Tracking
      if (won) {
        currentWinStreak++;
        if (currentWinStreak > maxWinStreak) maxWinStreak = currentWinStreak;
      } else {
        currentWinStreak = 0;
      }

      // Consecutive Scoring Games Tracking
      if (bosScore > 0) {
        currentScoreStreak++;
        if (currentScoreStreak > maxScoreStreak) maxScoreStreak = currentScoreStreak;
      } else {
        currentScoreStreak = 0;
      }

      // Single Game Highs
      if (bosScore > mostRuns) mostRuns = bosScore;
      if (bosScore - oppScore > largestMargin) largestMargin = bosScore - oppScore;
      if (won && oppScore === 0) shutouts++;
    });

    return { maxWinStreak, maxScoreStreak, mostRuns, largestMargin, shutouts };
  }, [games]);

  if (!milestones) return null;

  return (
    <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
      <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)] flex items-center gap-2">
        <SafeIcon icon={FiStar} size={14} className="text-[#d8ceb3]" />
        <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Season Milestones & Streaks</span>
      </div>
      <div className="p-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
        <MilestoneBox icon={FiTrendingUp} title="Longest Win Streak" value={`${milestones.maxWinStreak}G`} color="#a9c9b1" sub="Consecutive Wins" />
        <MilestoneBox icon={FiActivity} title="Scoring Streak" value={`${milestones.maxScoreStreak}G`} color="#d8ceb3" sub="Games with 1+ Runs" />
        <MilestoneBox icon={FiTarget} title="Most Runs (Game)" value={milestones.mostRuns} color="#e85555" sub="Season High" />
        <MilestoneBox icon={FiStar} title="Largest Margin" value={`+${milestones.largestMargin}`} color="#a9c9b1" sub="Run Differential" />
        <MilestoneBox icon={FiShield} title="Shutout Wins" value={milestones.shutouts} color="#d8ceb3" sub="Opponent scored 0" />
      </div>
    </div>
  );
};

export default SeasonMilestones;